/**
 * Whether the one-time live-plan note is on screen, and the dismissal that retires it.
 *
 * The flag is read ONCE, when the hook first runs, rather than on every render: a dismissal has to
 * take the note off the screen immediately, and re-reading storage while the component re-renders
 * would answer with whatever was last written rather than with the state the trainer is in.
 *
 * Mobile's `useLivePlanNote`, minus its resolving gate — `localStorage` answers synchronously, so
 * there is nothing to wait for and the note cannot flash to a returning trainer.
 */

import { useState } from 'react';

import { hasSeenLiveNote, markLiveNoteSeen } from '../model/liveNote';

import { preferenceStore } from '~/shared/lib/preferenceStore';

export interface LiveNoteState {
  /** True on a running fork the trainer has not dismissed the note on. */
  readonly visible: boolean;
  readonly dismiss: () => void;
}

export function useLiveNote(isLivePlan: boolean): LiveNoteState {
  const [seen, setSeen] = useState(() => hasSeenLiveNote(preferenceStore()));

  return {
    visible: isLivePlan && !seen,
    dismiss: () => {
      setSeen(true);
      markLiveNoteSeen(preferenceStore());
    },
  };
}
